import { useEffect, useState } from "react";
import {
  Database,
  Globe,
  LayoutTemplate,
  RefreshCw,
  Shield,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { functionsService, isFirebaseConfigured } from "../services/firebase";

type ManagedSite = {
  siteId: string;
  siteName: string;
  status: string;
  htmlSize: number;
  publicUrl?: string | null;
  paidUntil?: number;
  daysRemaining?: number;
};
type Renewal = {
  siteId: string;
  siteName: string;
  ownerId: string;
  status: string;
  paidUntil: number;
  daysRemaining: number;
};
type ManagedUser = {
  uid: string;
  email: string;
  displayName: string;
  role: string;
  sites: ManagedSite[];
};
type Billing = {
  monthlyPriceMinor: number;
  extraSlotPriceMinor: number;
  qrImageUrl: string;
  paymentInstructions: string;
  alternatePaymentInstructions: string;
  payeeName: string;
};
type Usage = {
  sites: number;
  htmlBytes: number;
  templates: number;
  measured: string;
};
type SitePreview = {
  siteId: string;
  siteName: string;
  htmlContent: string;
  publicUrl: string | null;
  status: string;
};
type Payment = Record<string, unknown> & {
  paymentRequestId: string;
  status: string;
  amountMinor: number;
  paymentDetails?: Partial<Billing>;
  paymentReference?: string;
  paymentMethod?: string;
  ownerId?: string;
  kind?: string;
};
const MAX_QR_CHARS = 380000;
async function compressQr(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("Gagal membaca imej QR."));
      element.src = url;
    });
    const scale = Math.min(1, 512 / Math.max(image.width, image.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Pelayar ini tidak menyokong pemprosesan imej.");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    let dataUrl = canvas.toDataURL("image/png");
    if (dataUrl.length > MAX_QR_CHARS) dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    if (dataUrl.length > MAX_QR_CHARS) throw new Error("Imej QR terlalu besar. Sila gunakan imej QR yang lebih ringkas.");
    return dataUrl;
  } finally {
    URL.revokeObjectURL(url);
  }
}
const initialBilling: Billing = {
  monthlyPriceMinor: 0,
  extraSlotPriceMinor: 0,
  qrImageUrl: "",
  paymentInstructions: "",
  alternatePaymentInstructions: "",
  payeeName: "",
};

export default function AdminPage() {
  const [monthlyRm, setMonthlyRm] = useState("0.00");
  const [extraRm, setExtraRm] = useState("0.00");
  const [users, setUsers] = useState<ManagedUser[]>([]),
    [usage, setUsage] = useState<Usage | null>(null),
    [payments, setPayments] = useState<Payment[]>([]),
    [settings, setSettings] = useState(initialBilling),
    [message, setMessage] = useState(""),
    [loading, setLoading] = useState(false),
    [busy, setBusy] = useState(""),
    [preview, setPreview] = useState<SitePreview | null>(null);
  const [renewals, setRenewals] = useState<Renewal[]>([]);
  const [template, setTemplate] = useState<Record<string, unknown>>({
    name: "",
    category: "business",
    description: "",
    tags: [],
    rating: 5,
    usageCount: 0,
    isPremium: false,
    htmlContent: "",
    variables: [],
  });
  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setMessage("");
    try {
      await fn();
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy("");
    }
  };
  const load = async () => {
    setLoading(true);
    try {
      const [u, m, p, b] = await Promise.all([
        functionsService.adminListUsers(),
        functionsService.adminGetUsage(),
        functionsService.adminListPayments(),
        functionsService.adminGetBillingSettings(),
      ]);
      setUsers(u.users);
      setUsage(m);
      setPayments(p.requests as Payment[]);
      try {
        setRenewals((await functionsService.adminListRenewals()).renewals as Renewal[]);
      } catch {
        setRenewals([]);
      }
      if (b.settings) {
        const data = b.settings;
        setMonthlyRm((data.monthlyPriceMinor / 100).toFixed(2));
        setExtraRm((data.extraSlotPriceMinor / 100).toFixed(2));
        setSettings({ monthlyPriceMinor: data.monthlyPriceMinor,
          extraSlotPriceMinor: data.extraSlotPriceMinor, qrImageUrl: data.qrImageUrl,
          payeeName: data.payeeName, paymentInstructions: data.paymentInstructions || "",
          alternatePaymentInstructions: data.alternatePaymentInstructions || "" });
      }
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isFirebaseConfigured) void load();
  }, []);
  const saveBilling = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || loading) return;
    if (!settings.qrImageUrl.trim()) {
      setMessage("Sila muat naik imej QR sehingga selesai sebelum menyimpan.");
      return;
    }
    return run("billing", async () => {
      await functionsService.adminUpdateBillingSettings({
        ...settings,
        monthlyPriceMinor: Math.round(Number(monthlyRm) * 100),
        extraSlotPriceMinor: Math.round(Number(extraRm) * 100),
        currency: "MYR",
      });
      setMessage("Tetapan bayaran disimpan.");
    });
  };
  const uploadQr = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      setMessage("QR mesti dalam format PNG atau JPEG.");
      return;
    }
    setBusy("qr");
    setMessage("");
    void compressQr(file)
      .then((dataUrl) => {
        setSettings((s) => ({ ...s, qrImageUrl: dataUrl }));
        setMessage("Imej QR sedia. Tekan Simpan tetapan bayaran untuk mengaktifkannya.");
      })
      .catch((err) => setMessage((err as Error).message))
      .finally(() => setBusy(""));
  };
  if (!isFirebaseConfigured)
    return (
      <div className="p-10 text-center">
        Admin panel memerlukan Firebase sebenar.
      </div>
    );
  return (
    <main className="min-h-screen bg-surface-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <header>
          <div className="flex items-center gap-3">
            <Shield className="text-telegram" />
            <h1 className="text-3xl font-extrabold">Pusat Pentadbiran</h1>
          </div>
          <p className="text-surface-500">
            Urus pengguna berdaftar, laman, reka bentuk dan bayaran.
          </p>
        </header>
        {message && (
          <div className="card p-4 text-sm text-red-700">{message}</div>
        )}
        <section className="grid sm:grid-cols-3 gap-4">
          {[
            [Users, "Pengguna", users.length],
            [Globe, "Laman", usage?.sites || 0],
            [
              Database,
              "HTML tersimpan",
              `${((usage?.htmlBytes || 0) / 1024).toFixed(1)} KB`,
            ],
          ].map(([Icon, label, value]) => {
            const MetricIcon = Icon as typeof Users;
            return (
              <div className="card p-5" key={String(label)}>
                <MetricIcon size={20} className="text-telegram mb-2" />
                <div className="text-2xl font-bold">{String(value)}</div>
                <div className="text-sm text-surface-500">{String(label)}</div>
              </div>
            );
          })}
        </section>
        <section className="card p-6">
          <div className="flex justify-between mb-4">
            <h2 className="font-bold flex gap-2">
              <Users size={18} /> Pengguna berdaftar & laman
            </h2>
            <button
              className="btn-outline btn-sm"
              disabled={loading}
              onClick={() => void load()}
            >
              <RefreshCw size={14} /> Muat semula
            </button>
          </div>
          {users.map((u) => (
            <div className="border-t py-4" key={u.uid}>
              <b>{u.displayName || u.email}</b>{" "}
              <span className="badge-gray text-xs">{u.role}</span>
              <div className="text-sm text-surface-500">{u.email}</div>
              {u.sites.map((s) => (
                <div
                  className="flex items-center justify-between bg-surface-50 rounded-lg p-3 mt-2"
                  key={s.siteId}
                >
                  <span>
                    <b>{s.siteName}</b>{" "}
                    <span className="text-xs">{s.status}</span>{" "}
                    {s.status !== "draft" && (() => {
                      const d = s.daysRemaining ?? 0;
                      if (!s.paidUntil || d <= 0) {
                        return <span className="text-xs text-red-600">· tamat</span>;
                      }
                      return (
                        <span className={d <= 5 ? "text-xs text-amber-600" : "text-xs text-surface-500"}>
                          · {new Date(s.paidUntil).toLocaleDateString("ms-MY")} ({d} hari)
                        </span>
                      );
                    })()}
                  </span>
                  <span className="flex gap-2">
                    <button
                      className="btn-outline btn-sm"
                      onClick={() =>
                        void run("site", async () =>
                          setPreview(
                            await functionsService.adminGetSite({
                              siteId: s.siteId,
                            }),
                          ),
                        )
                      }
                    >
                      Lihat
                    </button>
                    <button
                      className="text-red-600 p-2"
                      onClick={() =>
                        window.confirm("Padam laman ini secara kekal?") &&
                        void run("delete", async () => {
                          await functionsService.adminDeleteSite({
                            siteId: s.siteId,
                          });
                          await load();
                        })
                      }
                    >
                      <Trash2 size={16} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          ))}
        </section>
        <section className="card p-6">
          <h2 className="font-bold mb-1">Pembaharuan hampir tamat</h2>
          <p className="text-sm text-surface-500 mb-3">
            Laman yang tamat dalam 7 hari atau sudah tamat tempoh.
          </p>
          {renewals.length === 0 ? (
            <p className="text-sm text-surface-500">Tiada laman yang hampir tamat.</p>
          ) : (
            renewals.map((r) => (
              <div
                className="border-t py-3 flex items-center justify-between gap-4"
                key={r.siteId}
              >
                <span className="text-sm">
                  <b>{r.siteName}</b> · <span className="text-xs">{r.status}</span>
                  <br />
                  <small className="text-surface-500">
                    Pemilik: {r.ownerId} ·{" "}
                    {r.paidUntil
                      ? new Date(r.paidUntil).toLocaleDateString("ms-MY")
                      : "tiada tarikh"}
                    {r.daysRemaining > 0 ? ` (${r.daysRemaining} hari)` : " (tamat)"}
                  </small>
                </span>
              </div>
            ))
          )}
        </section>
        <section className="card p-6">
          <h2 className="font-bold flex gap-2 mb-4">
            <LayoutTemplate size={18} /> Koleksi reka bentuk
          </h2>
          <p className="text-sm text-surface-500">
            Tambah atau ubah dokumen <code>templates</code>.
          </p>
          <input
            className="input mb-2"
            placeholder="Nama"
            value={String(template.name)}
            onChange={(e) => setTemplate({ ...template, name: e.target.value })}
          />
          <textarea
            className="input mb-2 min-h-32"
            placeholder="HTML template"
            value={String(template.htmlContent)}
            onChange={(e) =>
              setTemplate({ ...template, htmlContent: e.target.value })
            }
          />
          <button
            className="btn-primary btn-md"
            disabled={!!busy}
            onClick={() =>
              void run("template", async () => {
                await functionsService.adminUpsertTemplate(template);
                setMessage("Reka bentuk disimpan.");
              })
            }
          >
            Simpan reka bentuk
          </button>
        </section>
        <section className="card p-6">
          <h2 className="font-bold mb-3">Semakan pembayaran</h2>
          {payments.map((p) => (
            <div
              className="border-t py-3 flex justify-between gap-4"
              key={p.paymentRequestId}
            >
              <span>
                Pemilik: {p.ownerId || "-"} · Kaedah: {p.paymentMethod || "-"} ·
                RM {(p.amountMinor / 100).toFixed(2)} · Status: {p.status}
                <br />
                <small>
                  Rujukan: {p.paymentReference || "-"} · Jenis: {p.kind || "-"}{" "}
                  · Penerima: {p.paymentDetails?.payeeName || "-"} · Arahan:{" "}
                  {p.paymentDetails?.paymentInstructions || "-"}
                  {p.paymentDetails?.alternatePaymentInstructions &&
                    ` · Alternatif: ${p.paymentDetails.alternatePaymentInstructions}`}
                </small>
              </span>
              {p.status === "pending" && (
                <button
                  className="btn-primary btn-sm"
                  disabled={!!busy}
                  onClick={() => {
                    const ref = window.prompt("Rujukan transaksi bank");
                    if (ref)
                      void run("approve", async () => {
                        await functionsService.approvePayment({
                          paymentRequestId: p.paymentRequestId,
                          transactionReference: ref,
                        });
                        await load();
                      });
                  }}
                >
                  Lulus
                </button>
              )}
            </div>
          ))}
        </section>
        <form className="card p-6 space-y-3" onSubmit={saveBilling}>
          <h2 className="font-bold">Tetapan bayaran</h2>
          <p className="text-sm text-surface-600">Masukkan amaun dalam RM, contohnya 10.50. Harga ini dikenakan bagi SETIAP laman sebulan; laman yang tidak diperbaharui akan diambil offline.</p>
          <label className="block text-sm">Harga sebulan per laman (RM)
            <input className="input mt-1" type="number" min="0.01" max="1000000" step="0.01" required
              disabled={!!busy || loading} value={monthlyRm} onChange={e => setMonthlyRm(e.target.value)} />
          </label>
          {message && <p role="status" className="text-sm whitespace-pre-wrap">{message}</p>}
          {(
            [
              "payeeName",
              "paymentInstructions",
              "alternatePaymentInstructions",
            ] as const
          ).map((k) => (
            <label className="block text-sm" key={k}>
              {{monthlyPriceMinor: 'Harga bulanan (sen)', extraSlotPriceMinor: 'Harga laman tambahan (sen)', payeeName: 'Nama penerima', paymentInstructions: 'Arahan bayaran', alternatePaymentInstructions: 'Arahan pembayaran alternatif'}[k]}
              <input
                className="input mt-1"
                type={k.includes("Price") ? "number" : "text"}
                min={k.includes("Price") ? 1 : undefined}
                max={k.includes("Price") ? 100000000 : undefined}
                step={k.includes("Price") ? 1 : undefined}
                required={k.includes("Price") || k === "payeeName"}
                maxLength={k === "payeeName" ? 200 : 4000}
                disabled={!!busy || loading}
                value={settings[k]}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    [k]: k.includes("Price")
                      ? Number(e.target.value)
                      : e.target.value,
                  })
                }
              />
            </label>
          ))}
          <label className="block text-sm" htmlFor="payment-qr">Muat naik imej QR (PNG/JPEG, maksimum 1 MB)</label>
          <input
            id="payment-qr"
            type="file"
            accept="image/png,image/jpeg"
            disabled={!!busy || loading}
            onChange={uploadQr}
          />
          {settings.qrImageUrl && (
            <img
              className="max-w-xs rounded"
              src={settings.qrImageUrl}
              alt="QR bayaran"
            />
          )}
           <button className="btn-primary btn-md" disabled={!!busy || loading}>
             {busy === "qr" ? "Memuat naik QR..." : busy === "billing" ? "Menyimpan..." : "Simpan tetapan bayaran"}
          </button>
        </form>
        {preview && (
          <div className="fixed inset-0 bg-black/60 p-6 z-10">
            <div className="bg-white h-full max-w-5xl mx-auto p-3">
              <button onClick={() => setPreview(null)}>
                <X />
              </button>
              <iframe
                title={preview.siteName}
                className="w-full h-[calc(100%-2rem)]"
                sandbox="allow-scripts"
                referrerPolicy="no-referrer"
                srcDoc={preview.htmlContent}
              />
              {typeof preview.publicUrl === "string" &&
                /^https:\/\/[^\s]+$/.test(preview.publicUrl) && (
                  <a href={preview.publicUrl} target="_blank" rel="noreferrer">
                    Buka URL awam
                  </a>
                )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
